// Deterministic PlantUML splitter.
//
// The design agent assembles one @startuml…@enduml with a shared HEADER
// (title / skinparams / participants) followed by "== Flow Name ==" sections.
// We split that big diagram into one standalone sub-diagram per flow so each
// renders small and legible — no LLM, purely by the existing section markers.

const SECTION_RE = /^\s*==\s*(.+?)\s*==\s*$/;

// Strip the @startuml / @enduml wrappers (and a leading "plantuml" fence word).
function stripWrappers(code) {
  return String(code || "")
    .replace(/^\s*plantuml\s*/i, "")
    .replace(/^\s*@startuml[^\n]*\n?/i, "")
    .replace(/\n?@enduml\s*$/i, "")
    .replace(/\s+$/, "");
}

// Count how many "== Flow ==" sections a diagram has.
export function countFlowSections(code) {
  const body = stripWrappers(code);
  let n = 0;
  for (const line of body.split("\n")) if (SECTION_RE.test(line)) n++;
  return n;
}

// True when there are ≥2 flow sections (i.e. splitting is meaningful).
export function hasFlowSections(code) {
  return countFlowSections(code) >= 2;
}

// Split into [{ title, plantuml }]. Each sub-diagram = header + one flow section,
// wrapped in its own @startuml…@enduml. Falls back to a single entry when there
// is nothing to split.
export function splitPlantumlByFlow(code) {
  const body = stripWrappers(code);
  const lines = body.split("\n");

  // Header = everything before the first "== … ==" line.
  let firstIdx = lines.findIndex((l) => SECTION_RE.test(l));
  if (firstIdx === -1) {
    return [{ title: "Diagram", plantuml: `@startuml\n${body}\n@enduml` }];
  }
  const header = lines.slice(0, firstIdx).join("\n").replace(/\s+$/, "");

  const sections = [];
  let cur = null;
  for (let i = firstIdx; i < lines.length; i++) {
    const m = lines[i].match(SECTION_RE);
    if (m) {
      if (cur) sections.push(cur);
      cur = { title: m[1].trim(), bodyLines: [lines[i]] };
    } else if (cur) {
      cur.bodyLines.push(lines[i]);
    }
  }
  if (cur) sections.push(cur);

  if (sections.length < 2) {
    return [{ title: "Diagram", plantuml: `@startuml\n${body}\n@enduml` }];
  }

  return sections.map((s) => {
    const sectionBody = s.bodyLines.join("\n").replace(/\s+$/, "");
    const parts = ["@startuml"];
    if (header) parts.push(header);
    parts.push(sectionBody, "@enduml");
    return { title: s.title, plantuml: parts.join("\n") };
  });
}

// The same "large" rule ZoomableDiagram uses (>4000 chars OR >80 lines).
export function isLargePuml(code) {
  return !!code && (code.length > 4000 || code.split("\n").length > 80);
}
